import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import {
  Search, X, ChevronDown, Building2, Filter, Calendar, GraduationCap,
  MapPin, BookOpen, Coins, Award, SlidersHorizontal,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------- Filter helpers ----------------------------------------------------
const TUITION_BUCKETS = [
  { id: 'lt5000', label: 'Less than 5,000', test: (v) => v != null && v < 5000 },
  { id: '5_10', label: '5,000 – 10,000', test: (v) => v >= 5000 && v < 10000 },
  { id: '10_15', label: '10,000 – 15,000', test: (v) => v >= 10000 && v < 15000 },
  { id: 'gt15', label: 'Over 15,000', test: (v) => v >= 15000 },
];
const LEARNING_MODES = ['On Campus', 'Online', 'Hybrid'];

// Map degree / course_level → study level bucket
const studyLevelOf = (m) => {
  const v = (m.course_level || m.degree || m.degree_type || '').toLowerCase();
  if (/(master|mba|msc|m\.sc|ma\b|m\.a|mphil|mph|mres|meng|m\.eng|llm|pgdip|pg.*cert|post.*grad|phd|d\.phil|doctor)/i.test(v)) return 'Postgraduate';
  if (/(bachelor|b\.sc|bsc|b\.a|ba\b|b\.eng|beng|b\.tech|btech|bba|bcom|llb|m?bbs|under.*grad|undergraduate|foundation|diploma|hnd|hnc|associate)/i.test(v)) return 'Undergraduate';
  return '';
};

// Does a program's degree string match the user's filter value?
// Handles synonyms so picking "Bachelors" matches BEng/BSc/BA/BBA/LLB/BTech etc.
// and "Masters" matches MSc/MA/MBA/MEng/LLM/MRes etc.
const degreeMatchesFilter = (programDegree, filterValue) => {
  if (!filterValue) return true;
  const dRaw = String(programDegree || '').toLowerCase().trim();
  if (!dRaw) return false;
  const fRaw = String(filterValue).toLowerCase().trim();
  if (dRaw.includes(fRaw) || fRaw.includes(dRaw)) return true;

  const BACHELOR_RE = /(bachelor|under.*grad|undergraduate|b\.sc|bsc|b\.a|^ba\b|\bba\b|b\.eng|beng|b\.tech|btech|bba|bcom|llb|^mbbs|foundation|diploma|hnd|hnc|associate)/;
  const MASTER_RE = /(master|post.*grad|postgraduate|msc|m\.sc|^ma\b|\bma\b|m\.a|mba|meng|m\.eng|mres|mphil|mph|llm|pgdip|pg.*cert)/;
  const DOCTOR_RE = /(phd|doctor|d\.phil|dphil)/;

  const dIsBachelor = BACHELOR_RE.test(dRaw);
  const dIsMaster = MASTER_RE.test(dRaw);
  const dIsDoctor = DOCTOR_RE.test(dRaw);
  const fIsBachelor = BACHELOR_RE.test(fRaw);
  const fIsMaster = MASTER_RE.test(fRaw);
  const fIsDoctor = DOCTOR_RE.test(fRaw);

  return (
    (fIsBachelor && dIsBachelor) ||
    (fIsMaster && dIsMaster) ||
    (fIsDoctor && dIsDoctor)
  );
};

// Pull a min tuition number for filtering / sorting
const minTuition = (m) => {
  const br = m.tuition_breakdown || [];
  const nums = br.map((b) => b?.amount).filter((n) => typeof n === 'number');
  if (nums.length) return Math.min(...nums);
  const raw = (m.annual_tuition_fees_raw || '').toString();
  const match = raw.match(/[\d,]{3,}/);
  return match ? Number(match[0].replace(/,/g, '')) : null;
};

const countryFromCampus = (m) => {
  const s = `${m.campus_name || ''} ${m.university_name || ''}`;
  if (/(UK|United Kingdom|England|Scotland|Wales|London|York|Manchester)/i.test(s)) return 'United Kingdom';
  if (/(US|USA|United States|New York|California|Massachusetts|Boston)/i.test(s)) return 'United States';
  if (/(Canada|Toronto|Ontario|Quebec|Vancouver)/i.test(s)) return 'Canada';
  if (/(Australia|Sydney|Melbourne|Brisbane)/i.test(s)) return 'Australia';
  if (/(Cyprus)/i.test(s)) return 'Cyprus';
  if (/(Ireland|Dublin)/i.test(s)) return 'Ireland';
  if (/(Poland|Warsaw|Krakow)/i.test(s)) return 'Poland';
  if (/(France|Paris)/i.test(s)) return 'France';
  if (/(Germany|Berlin|Munich)/i.test(s)) return 'Germany';
  return 'Other';
};

const initials = (n) =>
  (n || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// ---------------------------------------------------------------------------
const CoursesListPage = () => {
  const [params, setParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Top search bar
  const [degreeTopFilter, setDegreeTopFilter] = useState(params.get('degree') || params.get('degree_type') || '');
  const [locationTopFilter, setLocationTopFilter] = useState(params.get('country') || '');
  const [subjectTopFilter, setSubjectTopFilter] = useState(params.get('subject') || '');

  // Sidebar filters
  const [studyLevels, setStudyLevels] = useState([]);                 // ['Postgraduate']
  const [destinations, setDestinations] = useState(
    params.get('country') ? [params.get('country')] : []
  );
  const [tuitionBuckets, setTuitionBuckets] = useState([]);
  const [learningModes, setLearningModes] = useState([]);

  const [sort, setSort] = useState('price_asc');                       // price_asc / price_desc / name

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/programs`);
        setPrograms(Array.isArray(r.data) ? r.data : []);
      } catch { setPrograms([]); }
      setLoading(false);
    };
    run();
  }, []);

  // Distinct values shown in the sidebar
  const allCountries = useMemo(() => {
    const set = new Set(programs.map((p) => countryFromCampus(p.metadata || {})));
    set.delete('Other');
    return [...set].sort();
  }, [programs]);

  // Apply filters
  const filtered = useMemo(() => {
    const q = (params.get('q') || '').toLowerCase();
    let list = programs.filter((p) => {
      const m = p.metadata || {};
      // Top bar
      if (degreeTopFilter && !degreeMatchesFilter(m.degree_type || m.degree, degreeTopFilter)) return false;
      if (locationTopFilter) {
        if (countryFromCampus(m).toLowerCase() !== locationTopFilter.toLowerCase()) return false;
      }
      if (subjectTopFilter && !(m.subject || '').toLowerCase().includes(subjectTopFilter.toLowerCase())) return false;
      // Query
      if (q) {
        const hay = [
          p.name, m.course_title, m.subject, m.university_name,
          ...(m.search_keywords || []),
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Sidebar
      if (studyLevels.length && !studyLevels.includes(studyLevelOf(m))) return false;
      if (destinations.length && !destinations.includes(countryFromCampus(m))) return false;
      if (tuitionBuckets.length) {
        const v = minTuition(m);
        if (v == null) return false;
        if (!tuitionBuckets.some((id) => TUITION_BUCKETS.find((b) => b.id === id)?.test(v))) return false;
      }
      if (learningModes.length && !learningModes.includes(m.delivery_method)) return false;
      return true;
    });
    if (sort === 'price_asc') list.sort((a, b) => (minTuition(a.metadata || {}) ?? Infinity) - (minTuition(b.metadata || {}) ?? Infinity));
    if (sort === 'price_desc') list.sort((a, b) => (minTuition(b.metadata || {}) ?? -Infinity) - (minTuition(a.metadata || {}) ?? -Infinity));
    if (sort === 'name') list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [programs, params, degreeTopFilter, locationTopFilter, subjectTopFilter,
       studyLevels, destinations, tuitionBuckets, learningModes, sort]);

  // Active chip helpers
  const chips = [
    locationTopFilter && { k: 'country', label: `Country: ${locationTopFilter}`, clear: () => setLocationTopFilter('') },
    subjectTopFilter && { k: 'subject', label: `Subject: ${subjectTopFilter}`, clear: () => setSubjectTopFilter('') },
    degreeTopFilter && { k: 'degree', label: `Degree: ${degreeTopFilter}`, clear: () => setDegreeTopFilter('') },
    ...studyLevels.map((lvl) => ({ k: `lvl-${lvl}`, label: `Level: ${lvl}`, clear: () => setStudyLevels((arr) => arr.filter((x) => x !== lvl)) })),
    ...destinations.map((d) => ({ k: `dst-${d}`, label: `Destination: ${d}`, clear: () => setDestinations((arr) => arr.filter((x) => x !== d)) })),
    ...tuitionBuckets.map((bid) => {
      const b = TUITION_BUCKETS.find((x) => x.id === bid);
      return { k: `bdg-${bid}`, label: `Budget: ${b?.label || bid}`, clear: () => setTuitionBuckets((arr) => arr.filter((x) => x !== bid)) };
    }),
    ...learningModes.map((lm) => ({ k: `lm-${lm}`, label: `Mode: ${lm}`, clear: () => setLearningModes((arr) => arr.filter((x) => x !== lm)) })),
  ].filter(Boolean);

  const onSearch = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams(params);
    if (degreeTopFilter) { next.set('degree', degreeTopFilter); next.delete('degree_type'); } else { next.delete('degree'); next.delete('degree_type'); }
    if (locationTopFilter) next.set('country', locationTopFilter); else next.delete('country');
    if (subjectTopFilter) next.set('subject', subjectTopFilter); else next.delete('subject');
    setParams(next, { replace: true });
  };

  return (
    <PublicLayout>
      {/* Top search row */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <form onSubmit={onSearch} className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="courses-top-search">
          <TopSelect label="Select a degree type" value={degreeTopFilter} onChange={setDegreeTopFilter}
            options={[...new Set(programs.map((p) => p.metadata?.degree_type || p.metadata?.degree).filter(Boolean))]}
            className="md:col-span-3" testid="top-degree-select" />
          <TopSelect label="Select for a location" value={locationTopFilter} onChange={(v) => { setLocationTopFilter(v); setDestinations(v ? [v] : []); }}
            options={allCountries}
            className="md:col-span-3" testid="top-location-select" />
          <TopSelect label="Select for a subject" value={subjectTopFilter} onChange={setSubjectTopFilter}
            options={[...new Set(programs.map((p) => p.metadata?.subject).filter(Boolean))]}
            className="md:col-span-3" testid="top-subject-select" />
          <button
            type="submit"
            className="md:col-span-3 bg-[#1A2A4F] text-white font-bold rounded-md hover:brightness-95 transition flex items-center justify-center gap-2"
            data-testid="top-find-program-btn"
          >
            <Search className="w-4 h-4" /> Find Program
          </button>
        </form>
      </div>

      {/* Body grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* SIDEBAR */}
        <aside className="bg-white border border-gray-100 rounded-2xl p-5 h-fit lg:sticky lg:top-32" data-testid="filters-sidebar">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1A2A4F] flex items-center gap-2 text-base">
              <SlidersHorizontal className="w-4 h-4 text-[#F5A623]" /> Filters
            </h2>
            {chips.length > 0 && (
              <button
                onClick={() => { setStudyLevels([]); setDestinations([]); setTuitionBuckets([]); setLearningModes([]); setDegreeTopFilter(''); setLocationTopFilter(''); setSubjectTopFilter(''); }}
                className="text-[11px] font-semibold text-[#F5A623] hover:underline"
                data-testid="clear-all-filters"
              >Clear all</button>
            )}
          </div>

          <FilterGroup title="Study level">
            {['Undergraduate', 'Postgraduate'].map((lvl) => (
              <RadioRow key={lvl} label={lvl} checked={studyLevels.includes(lvl)}
                onToggle={() => setStudyLevels((arr) => arr.includes(lvl) ? arr.filter((x) => x !== lvl) : [...arr, lvl])}
                testid={`filter-level-${lvl.toLowerCase()}`} />
            ))}
          </FilterGroup>

          <FilterGroup title="Study destination">
            {allCountries.length === 0 && <p className="text-xs text-gray-400">No data yet</p>}
            {allCountries.map((c) => (
              <RadioRow key={c} label={c} checked={destinations.includes(c)}
                onToggle={() => setDestinations((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c])}
                testid={`filter-destination-${c.toLowerCase().replace(/\s+/g, '-')}`} />
            ))}
          </FilterGroup>

          <FilterGroup title="Annual tuition budget" hint="Min figure in the program's annual tuition">
            {TUITION_BUCKETS.map((b) => (
              <RadioRow key={b.id} label={b.label} checked={tuitionBuckets.includes(b.id)}
                onToggle={() => setTuitionBuckets((arr) => arr.includes(b.id) ? arr.filter((x) => x !== b.id) : [...arr, b.id])}
                testid={`filter-budget-${b.id}`} />
            ))}
          </FilterGroup>

          <FilterGroup title="Learning mode">
            {LEARNING_MODES.map((m) => (
              <RadioRow key={m} label={m} checked={learningModes.includes(m)}
                onToggle={() => setLearningModes((arr) => arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m])}
                testid={`filter-mode-${m.toLowerCase().replace(/\s+/g, '-')}`} />
            ))}
          </FilterGroup>
        </aside>

        {/* RESULTS */}
        <section className="space-y-4">
          {/* Chips + Sort */}
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="results-toolbar">
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <span key={c.k} className="inline-flex items-center gap-1 bg-white border border-gray-200 text-[#1A2A4F] text-xs font-semibold rounded-full pl-3 pr-2 py-1" data-testid={`chip-${c.k}`}>
                  {c.label}
                  <button onClick={c.clear} className="ml-1 w-4 h-4 rounded-full hover:bg-gray-100 flex items-center justify-center" aria-label={`Remove ${c.label}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <SortDropdown value={sort} onChange={setSort} />
              <span className="text-gray-500 font-semibold" data-testid="results-count">
                {filtered.length} program{filtered.length === 1 ? '' : 's'} found
              </span>
            </div>
          </div>

          {/* Rows */}
          {loading ? (
            <p className="text-center text-gray-500 py-20" data-testid="results-loading">Loading programs…</p>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-12 text-center" data-testid="results-empty">
              <BookOpen className="mx-auto w-10 h-10 text-[#F5A623]" />
              <h3 className="mt-3 font-bold text-[#1A2A4F]">No programs match these filters</h3>
              <p className="mt-1 text-sm text-gray-500">Try removing a filter or changing your search.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="results-list">
              {filtered.map((p) => <ProgramRow key={p.id} program={p} />)}
            </ul>
          )}
        </section>
      </div>
    </PublicLayout>
  );
};

// -------- Subcomponents -----------------------------------------------------
const TopSelect = ({ label, value, onChange, options, className, testid }) => (
  <label className={`block ${className || ''}`}>
    <span className="block text-[11px] font-semibold text-gray-600 mb-1">{label}</span>
    <div className="relative">
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm appearance-none focus:ring-2 focus:ring-[#F5A623]/30 focus:border-[#F5A623] outline-none bg-white"
        data-testid={testid}
      >
        <option value="">{label}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown className="w-4 h-4 absolute right-2 top-3 text-gray-400 pointer-events-none" />
    </div>
  </label>
);

const FilterGroup = ({ title, hint, children }) => (
  <section className="border-t border-gray-100 first:border-t-0 first:pt-0 py-4">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-[#1A2A4F]/80">{title}:</h3>
      {hint && <span title={hint} className="text-[10px] text-gray-400">?</span>}
    </div>
    <div className="space-y-2">{children}</div>
  </section>
);

const RadioRow = ({ label, checked, onToggle, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid={testid}>
    <span className={`w-4 h-4 rounded-full border ${checked ? 'border-[#F5A623] bg-[#F5A623]' : 'border-gray-300'} flex items-center justify-center transition`}>
      {checked && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
    </span>
    <input type="checkbox" className="hidden" checked={checked} onChange={onToggle} />
    <span className="text-gray-700">{label}</span>
  </label>
);

const SortDropdown = ({ value, onChange }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none bg-white border border-gray-200 rounded-full pl-3 pr-7 py-1.5 text-xs font-semibold text-[#1A2A4F] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
      data-testid="sort-select"
    >
      <option value="price_asc">Price: Low to High</option>
      <option value="price_desc">Price: High to Low</option>
      <option value="name">Name: A → Z</option>
    </select>
    <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-2 text-gray-500 pointer-events-none" />
  </div>
);

const ProgramRow = ({ program }) => {
  const m = program.metadata || {};
  const slug = m.slug;
  const detailHref = slug ? `/courses/${slug}` : '#';
  const fee = minTuition(m);
  const feeText = fee != null ? `${m.currency || ''} ${fee.toLocaleString()}` : '—';
  const session = m.intake || m.session_start || '—';
  const sch = Array.isArray(m.scholarship) && m.scholarship.length ? m.scholarship[0] : (m.scholarship || '—');

  return (
    <li className="bg-white border border-gray-100 rounded-xl p-4 sm:p-5 hover:border-[#F5A623] hover:shadow-md transition" data-testid={`program-row-${program.id}`}>
      <div className="flex items-center gap-4">
        {/* Logo */}
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-md bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
          {m.hero_image ? (
            <img src={m.hero_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-bold text-[#1A2A4F]/60 tracking-wider">{initials(m.university_name)}</span>
          )}
        </div>
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-[#1A2A4F] truncate">{m.university_name || '—'}</p>
          <div className="mt-0.5 flex items-baseline gap-2 min-w-0">
            <h3 className="text-base font-semibold text-[#1A2A4F] truncate">{m.course_title || program.name}</h3>
            <span className="text-xs text-gray-500 whitespace-nowrap">{m.faculty || 'General'}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
            <InfoPill icon={MapPin} label="Campus" value={m.campus_name} />
            <InfoPill icon={Calendar} label="Duration" value={m.duration} />
            <InfoPill icon={GraduationCap} label="Sessions" value={session} />
            <InfoPill icon={Coins} label="Annual Fee" value={fee != null ? feeText : '0'} />
            <InfoPill icon={Coins} label="Currency" value={m.currency} />
            <InfoPill icon={Award} label="Scholarship" value={sch} />
            <InfoPill icon={GraduationCap} label="Degree" value={studyLevelOf(m) || m.degree_type || m.degree || '—'} />
          </div>
        </div>
        {/* Buttons */}
        <div className="hidden sm:flex flex-col gap-2 shrink-0">
          <Link to={detailHref}
            className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-4 py-2 rounded-md min-w-[150px] text-center transition"
            data-testid={`btn-details-${program.id}`}>
            Program Details
          </Link>
          <Link to={`/apply/${program.id}`}
            className="bg-[#1A2A4F] hover:brightness-110 text-white text-xs font-bold px-4 py-2 rounded-md min-w-[150px] text-center transition"
            data-testid={`btn-apply-${program.id}`}>
            Start Application
          </Link>
        </div>
      </div>
      {/* Mobile buttons */}
      <div className="sm:hidden mt-3 grid grid-cols-2 gap-2">
        <Link to={detailHref}
          className="bg-emerald-500 text-white text-xs font-bold px-3 py-2 rounded-md text-center"
          data-testid={`btn-details-mob-${program.id}`}>Program Details</Link>
        <Link to={`/apply/${program.id}`}
          className="bg-[#1A2A4F] text-white text-xs font-bold px-3 py-2 rounded-md text-center"
          data-testid={`btn-apply-mob-${program.id}`}>Start Application</Link>
      </div>
    </li>
  );
};

const InfoPill = ({ icon: Icon, label, value }) => (
  <span className="inline-flex items-center gap-1 bg-gray-50 text-gray-700 rounded px-2 py-1 max-w-full">
    {Icon && <Icon className="w-3 h-3 text-[#F5A623] shrink-0" />}
    <span className="text-gray-500">{label}:</span>
    <span className="font-semibold text-[#1A2A4F] truncate max-w-[140px]">{value || '—'}</span>
  </span>
);

export default CoursesListPage;
